import React, { useState, useEffect, useRef } from "react";
import { useCart } from "../../contexts/cartContext";
import { showErrorToast } from "../../../../utils/toast";
const EditCartItemModal = ({ item, onClose }) => {
    const { updateCartItem } = useCart();
    const [qty, setQty] = useState(String(item.qty));
    const [weight, setWeight] = useState(item.weight || "");
    const [error, setError] = useState("");

    const modalRef = useRef(null);
    const qtyInputRef = useRef(null);

    // Focus quantity input on open
    useEffect(() => {
        qtyInputRef.current?.focus();
    }, []);

    // Prevent background scroll while modal is open
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => (document.body.style.overflow = "auto");
    }, []);

    // Close modal when clicking outside
    const handleOutsideClick = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
            onClose();
        }
    };

    // Handle Enter key for saving
    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSave();
        if (e.key === "Escape") onClose();
    };

    const handleSave = () => {
        const parsedQty = Number(qty);
        if (!qty || Number.isNaN(parsedQty) || parsedQty <= 0) {
            setError("Quantity must be at least 1");
            return;
        }
        if (item.stock !== undefined && item.stock !== null && parsedQty > item.stock) {
            showErrorToast(`Only ${item.stock} left in stock`);
            return;
        }
        if (item.soldByWeight && weight < 0) {
            setError("Weight cannot be negative");
            return;
        }

        updateCartItem(item.lineId || item.id, { qty: parsedQty, weight });
        onClose();
    };

    return (
        <div
            className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
            onClick={handleOutsideClick}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
        >
            <div
                ref={modalRef}
                className="rounded-xl p-6 w-80 shadow-2xl bg-white"
            >
                <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)' }}>Edit {item.name}</h2>

                {/* Quantity */}
                <label className="block mb-2 text-sm font-medium" style={{ color: 'var(--color-text)' }}>Quantity</label>
                <input
                    ref={qtyInputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={qty}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^[0-9]+$/.test(val)) {
                            setQty(val);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (["e", "E", "+", "-", "."].includes(e.key)) {
                            e.preventDefault();
                        }
                    }}
                    className="w-full rounded-lg px-3 py-2 mb-4 border border-gray-300 focus:outline-none"
                    style={{ color: 'var(--color-text)' }}
                    onFocus={(e) => {
                        e.target.style.borderColor = 'var(--color-primary)';
                        e.target.style.boxShadow = `0 0 0 2px var(--color-primary)`;
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                    }}
                    min={1}
                />

                {/* Weight (optional) */}
                {item.soldByWeight && (
                    <>
                        <label className="block mb-2 text-sm font-medium" style={{ color: 'var(--color-text)' }}>Weight (g)</label>
                        <input
                            type="number"
                            value={weight}
                            onChange={(e) => setWeight(e.target.value)}
                            className="w-full rounded-lg px-3 py-2 mb-4 border border-gray-300 focus:outline-none"
                            style={{ color: 'var(--color-text)' }}
                            onFocus={(e) => {
                                e.target.style.borderColor = 'var(--color-primary)';
                                e.target.style.boxShadow = `0 0 0 2px var(--color-primary)`;
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#d1d5db';
                                e.target.style.boxShadow = 'none';
                            }}
                            min={0}
                        />
                    </>
                )}

                {/* Error message */}
                {error && <p className="text-sm mb-2 text-red-600">{error}</p>}

                {/* Actions */}
                <div className="flex justify-end gap-2 mt-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 focus:outline-none"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 rounded-lg transition-colors text-white focus:outline-none"
                        style={{ background: 'var(--color-primary)' }}
                        onMouseOver={e => e.currentTarget.style.background = 'var(--color-hover)'}
                        onMouseOut={e => e.currentTarget.style.background = 'var(--color-primary)'}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditCartItemModal;
